import { defineStore } from 'pinia'
import type { Ref } from 'vue'

// Export the main interface for component imports
export interface PaymentMethod {
  id: number
  name: string
  is_cash: boolean
  surcharge_percentage: number
  business_id: string
  created_by: string
  created_at: string
}

export interface PaymentMethodForm {
  name: string
  surcharge_percentage?: number
}

export const usePaymentMethodsStore = defineStore('paymentMethods', () => {
  const log = useLogger('store:payment-methods')
  const { get, post, put, delete: del } = useAuthenticatedFetch()
  const businessStore = useBusinessStore()
  const { businessId } = storeToRefs(businessStore)
  // State
  const paymentMethods: Ref<PaymentMethod[]> = ref([])
  const loading: Ref<boolean> = ref(false)

  const nuxtApp = useNuxtApp()
  const { t } = nuxtApp.$i18n
  const { $confirm } = nuxtApp

  // Core data fetching
  async function fetchItems(force = false): Promise<void> {
    if (!businessId.value) return

    // Skip fetch if data already exists and not forcing refresh
    if (!force && paymentMethods.value.length > 0) {
      log.info('Using cached payment methods')
      return
    }

    loading.value = true
    try {
      const res = await get(`/api/business/${businessId.value}/payment-methods`)
      paymentMethods.value = Array.isArray(res) ? res : (res.data || res.paymentMethods || [])
    }
    catch (error) {
      log.error('Error fetching payment methods:', error)
      useErrorToast('Failed to load payment methods')
    }
    finally {
      loading.value = false
    }
  }

  // CRUD operations
  async function createPaymentMethod(formData: PaymentMethodForm): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return false
    }

    try {
      const res = await post(`/api/business/${businessId.value}/payment-methods`, { ...formData, business_id: businessId.value })

      if (res.success !== false) {
        useSuccessToast('Payment method created successfully')
        await fetchItems(true)
        return true
      }
      else {
        useErrorToast(t('Failed to create payment method: ') + (res.message || 'Unknown error'))
        return false
      }
    }
    catch (error: unknown) {
      log.error('Error creating payment method:', error)
      const errorMessage = error instanceof Error ? error.message : 'Network error'
      useErrorToast(t('Failed to create payment method: ') + errorMessage)
      return false
    }
  }

  async function updatePaymentMethod(id: number, formData: PaymentMethodForm): Promise<boolean> {
    if (!businessId.value) {
      useErrorToast('Business ID not found')
      return false
    }

    try {
      const res = await put(`/api/business/${businessId.value}/payment-methods/${id}`, { ...formData, business_id: businessId.value })

      if (res.success !== false) {
        useSuccessToast('Payment method updated successfully')
        await fetchItems(true)
        return true
      }
      else {
        useErrorToast(t('Failed to update payment method: ') + (res.message || 'Unknown error'))
        return false
      }
    }
    catch (error: unknown) {
      log.error('Error updating payment method:', error)
      const errorMessage = error instanceof Error ? error.message : 'Network error'
      useErrorToast(t('Failed to update payment method: ') + errorMessage)
      return false
    }
  }

  async function deletePaymentMethod(item: PaymentMethod): Promise<boolean> {
    if (!businessId.value) return false
    return new Promise((resolve) => {
      $confirm.require({
        group: 'inline',
        message: t('Do you want to delete this payment method permanently?'),
        header: t('Delete Payment Method'),
        icon: 'pi pi-exclamation-triangle',
        rejectProps: {
          label: t('Cancel'),
          severity: 'secondary',
          outlined: true,
        },
        acceptProps: {
          label: t('Delete'),
          severity: 'danger',
        },
        accept: async () => {
          try {
            const res = await del(`/api/business/${businessId.value}/payment-methods/${item.id}`)
            if (res.success) {
              useSuccessToast('Payment method deleted successfully')
              await fetchItems(true)
              resolve(true)
            }
            else {
              useErrorToast('Failed to delete payment method')
              resolve(false)
            }
          }
          catch (error: unknown) {
            log.error('Error deleting payment method:', error)
            useErrorToast('Failed to delete payment method')
            resolve(false)
          }
        },
        reject: () => {
          // User cancelled deletion
          resolve(false)
        },
      })
    })
  }

  // Reset method to clear all state
  function reset(): void {
    paymentMethods.value = []
    loading.value = false
  }

  return {
    // State
    paymentMethods,
    loading,

    // Actions
    fetchItems,
    createPaymentMethod,
    updatePaymentMethod,
    deletePaymentMethod,

    // Reset method
    reset,
  }
}, {
  persist: {
    key: 'payment-methods',
    storage: localStorage,
    paths: ['paymentMethods'],
  },
})

export const createPaymentMethodsStore = async (businessIdParam: string) => {
  const paymentMethodsStore = usePaymentMethodsStore()
  const businessStore = useBusinessStore()

  businessStore.setBusinessId(businessIdParam)
  await paymentMethodsStore.fetchItems()

  return paymentMethodsStore
}
